import { useMemo } from 'react';
import { Check, Gift, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { InfoTip } from '@/components/ui/info-tip';
import { StatusPill } from '@/components/ui/status-pill';
import { SimpleSelect, type SimpleSelectOption as Option } from '@/components/ui/simple-select';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { DisplayService, ServicePricing } from '../../../../types/order.types';
import { formatPriceDisplay } from '@/util/formatting';
import noPropertyImg from '../../../../asset/images/no-property.svg';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ServiceCardProps {
  service: DisplayService;
  /** Minimum primaryServicePriority among all selected services */
  minSelectedPriority: number | null;
  onSelect: (slug: string) => void;
  onDeselect: (slug: string) => void;
  onQuantityChange: (slug: string, quantity: number) => void;
  /** Whether occupancy-radio is locked (a selection exists) */
  isDisabled?: boolean;
  /** DV-3204: the package comes with a free Basic Floor Plan. */
  offersFreeFloorPlan?: boolean;
}

// ---------------------------------------------------------------------------
// Quantity dropdown helpers
// ---------------------------------------------------------------------------

function buildQuantityOptions(
  map: Record<string, ServicePricing> | null,
  servicePriority: number | null,
  minSelectedPriority: number | null,
): Option[] {
  if (!map) return [];
  const prio = servicePriority ?? 999;
  const isPrimary =
    minSelectedPriority === null || prio <= minSelectedPriority;

  return Object.entries(map).map(([quantity, pricing]) => {
    const price = isPrimary ? pricing.totalPrice : pricing.totalAddOnPrice;
    return {
      // The value carries the quantity alone. Price is not round-tripped
      // through it: useServiceSelection re-derives every selectedPrice from
      // `service.map[selectedQuantity]` via recalculatePrices, so the
      // quantity is the only thing the card has to report.
      label: `${quantity} photos - $${formatPriceDisplay(price)}`,
      value: quantity,
    };
  });
}

function findSelectedOption(
  options: Option[],
  service: DisplayService,
): Option | undefined {
  const byQuantity = (quantity: number) =>
    options.find((o) => Number(o.value) === quantity);

  if (service.isSelected && service.selectedQuantity) {
    const match = byQuantity(service.selectedQuantity);
    if (match) return match;
  }
  if (service.defaultQuantity) {
    const match = byQuantity(service.defaultQuantity);
    if (match) return match;
  }
  return options[0];
}

// ---------------------------------------------------------------------------
// Price display
// ---------------------------------------------------------------------------

function PriceDisplay({
  service,
  minSelectedPriority,
}: {
  service: DisplayService;
  minSelectedPriority: number | null;
}) {
  const prio = service.primaryServicePriority ?? 999;
  const isPrimary =
    minSelectedPriority === null || prio <= minSelectedPriority;

  // For services with a quantity map, the price is shown in the dropdown
  if (service.map) return null;

  const pricing = service.defaultPricing;
  if (!pricing) return null;

  const price = isPrimary ? pricing.totalPrice : pricing.totalAddOnPrice;
  const initial = pricing.totalInitialPrice;
  const showSavings = initial > price;

  return (
    <div className="flex items-center gap-2 h-10">
      {showSavings && (
        <Typography
          variant="body"
          className="line-through text-muted-foreground"
        >
          ${formatPriceDisplay(initial)}
        </Typography>
      )}
      <Typography variant="body-semibold">${formatPriceDisplay(price)}</Typography>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ServiceCard
// ---------------------------------------------------------------------------

export function ServiceCard({
  service,
  minSelectedPriority,
  onSelect,
  onDeselect,
  onQuantityChange,
  offersFreeFloorPlan = false,
}: ServiceCardProps) {
  const slug = service.connectSlug ?? '';

  const quantityOptions = useMemo(
    () =>
      buildQuantityOptions(
        service.map,
        service.primaryServicePriority,
        minSelectedPriority,
      ),
    [service.map, service.primaryServicePriority, minSelectedPriority],
  );

  const selectedOption = useMemo(
    () => findSelectedOption(quantityOptions, service),
    [quantityOptions, service],
  );

  const handleQuantityChange = (
    _field: string,
    value: string | number,
  ) => {
    onQuantityChange(slug, Number(value));
    // Choosing a quantity on an unselected card selects it — through
    // onSelect, the same path as the Select button, so the hook runs the
    // full selection flow (add-on fetch, single-primary invariant).
    if (!service.isSelected) {
      onSelect(slug);
    }
  };

  const handleToggle = () => {
    if (service.isSelected) {
      onDeselect(slug);
    } else {
      if (selectedOption) {
        onQuantityChange(slug, Number(selectedOption.value));
      }
      onSelect(slug);
    }
  };

  // The card spans five rows of ServiceCardGrid's grid and shares them through
  // subgrid (image, name, description, floor plan badge, controls), so each
  // section lines up with the same section of the cards beside it, however
  // long a name or description runs. Every row is always rendered, empty or
  // not, so no section can slide into a neighbour's row.
  return (
    <div
      className={cn(
        'group relative row-span-5 grid grid-rows-subgrid gap-y-0 rounded-xl border overflow-hidden transition-all duration-200',
        service.isSelected
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border bg-card hover:shadow-lg',
      )}
    >
      {/* Most popular badge — overlaid on image */}
      {service.mostPopular && (
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground shadow-md">
            <Star className="h-3 w-3 fill-current" />
            Most popular
          </span>
        </div>
      )}

      {/* Service image */}
      <div className="h-40 xl:h-48 overflow-hidden bg-muted">
        <img
          src={service.onlineImage || noPropertyImg}
          alt={service.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      {/* Card body */}
      <div className="row-span-4 grid grid-rows-subgrid gap-y-0 bg-card p-4">
        {/* Name + info tooltip */}
        <div className="flex items-start justify-between gap-2">
          <Typography variant="heading-md" className="leading-tight">
            {service.name}
          </Typography>
          {service.onlineDescription && (
            <InfoTip
              label={`About ${service.name}`}
              side="bottom"
              triggerClassName="mt-0.5 cursor-help"
            >
              {service.onlineDescription}
            </InfoTip>
          )}
        </div>

        <div>
          {service.description && (
            <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
              {service.description}
            </p>
          )}
        </div>

        <div>
          {offersFreeFloorPlan && (
            <StatusPill variant="success" icon={Gift} className="mt-2">
              Free Basic Floor Plan Included
            </StatusPill>
          )}
        </div>

        {/* Controls — the last shared row, sized to the tallest card's
            controls; self-end keeps a card without a quantity dropdown
            bottom-aligned with one that has it. */}
        <div className="self-end pt-4">
          <div className="border-t border-border/40 pt-3 space-y-3">
            {/* Quantity dropdown */}
            {service.map && quantityOptions.length > 0 && (
              <SimpleSelect
                options={quantityOptions}
                name={`qty-${slug}`}
                controlId={`qty-${slug}`}
                ariaDescribedBy="quantity-select"
                maxMenuHeight={160}
                value={selectedOption}
                setFieldValue={handleQuantityChange}
                field={slug}
                additionalData={service}
              />
            )}

            {/* Price for fixed-price services */}
            <PriceDisplay
              service={service}
              minSelectedPriority={minSelectedPriority}
            />

            {/* Full-width CTA */}
            <Button
              variant={service.isSelected ? 'default' : 'outline'}
              onClick={handleToggle}
              className="w-full"
            >
              {service.isSelected && <Check className="h-4 w-4" />}
              {service.isSelected ? 'Selected' : 'Select'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
